import {
  MerchantApplicationAIPrefillingResponse,
  MerchantApplicationResponse,
} from 'types/responses/activation';

export const ACTIVATION_DATA_LOCAL_STORAGE_KEY = 'merchantApplicationData';

/**
 * The .NET API returns aiPrefilling fields in PascalCase.
 * Normalise to camelCase to match our TypeScript types.
 * Applied both on fresh API responses and when reading from localStorage
 * so stale PascalCase data is handled transparently.
 */
export const normaliseAIPrefilling = (
  raw: Record<string, unknown> | null | undefined
): MerchantApplicationAIPrefillingResponse | null => {
  if (!raw) return null;

  const rawInfo = (raw['BusinessInfo'] ?? raw['businessInfo']) as
    | Record<string, unknown>
    | null
    | undefined;

  if (!rawInfo) return null;

  const str = (key: string): string | null => {
    const v = rawInfo[key];
    return typeof v === 'string' ? v : null;
  };

  return {
    businessInfo: {
      url: str('Url') ?? str('url'),
      applicationId: str('ApplicationId') ?? str('applicationId'),
      franchiseGroup: str('FranchiseGroup') ?? str('franchiseGroup'),
      franchiseGroupConfidence:
        str('FranchiseGroupConfidence') ?? str('franchiseGroupConfidence'),
      multipleLocations: str('MultipleLocations') ?? str('multipleLocations'),
      multipleLocationsConfidence:
        str('MultipleLocationsConfidence') ??
        str('multipleLocationsConfidence'),
      avgTransactionValue:
        str('AvgTransactionValue') ?? str('avgTransactionValue'),
      avgTransactionValueConfidence:
        str('AvgTransactionValueConfidence') ??
        str('avgTransactionValueConfidence'),
      costOfLargestProduct:
        str('CostOfLargestProduct') ?? str('costOfLargestProduct'),
      costOfLargestProductConfidence:
        str('CostOfLargestProductConfidence') ??
        str('costOfLargestProductConfidence'),
      avgShipTime: str('AvgShipTime') ?? str('avgShipTime'),
      avgShipTimeConfidence:
        str('AvgShipTimeConfidence') ?? str('avgShipTimeConfidence'),
      shippingPolicy: str('ShippingPolicy') ?? str('shippingPolicy'),
      shippingPolicyConfidence:
        str('ShippingPolicyConfidence') ?? str('shippingPolicyConfidence'),
      returnPolicy: str('ReturnPolicy') ?? str('returnPolicy'),
      returnPolicyConfidence:
        str('ReturnPolicyConfidence') ?? str('returnPolicyConfidence'),
      businessLocation: str('BusinessLocation') ?? str('businessLocation'),
      businessLocationConfidence:
        str('BusinessLocationConfidence') ?? str('businessLocationConfidence'),
    },
  };
};

export type StoredActivationData = MerchantApplicationResponse & {
  lastUpdated?: number;
  businessDetailsStepAAIConfirmed?: boolean;
  businessDetailsStepBAIConfirmed?: boolean;
  businessDetailsStepCAIConfirmed?: boolean;
};

export const getStoredActivationData = (): StoredActivationData | null => {
  try {
    const stored = localStorage.getItem(ACTIVATION_DATA_LOCAL_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as StoredActivationData & {
      aiPrefilling?: Record<string, unknown> | null;
    };

    // Normalise aiPrefilling on every read so stale PascalCase data from
    // earlier sessions is handled transparently.
    return {
      ...parsed,
      aiPrefilling: normaliseAIPrefilling(
        parsed.aiPrefilling as Record<string, unknown>
      ),
    };
  } catch {
    return null;
  }
};

export const saveActivationDataToLocalStorage = (
  data: StoredActivationData | MerchantApplicationResponse
): void => {
  try {
    const storageData: StoredActivationData = {
      ...data,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(
      ACTIVATION_DATA_LOCAL_STORAGE_KEY,
      JSON.stringify(storageData)
    );
  } catch (err) {
    console.error('Failed to save application to localStorage:', err);
  }
};
